import { Component } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ClientsService } from '../../../../services/clients.service';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';
import { DeviceDetectorService } from '../../../../services/device-detector.service';
import { UserService } from '../../../../services/user';

@Component({
  selector: 'app-createclient',
  templateUrl: './createclient.component.html',
  styleUrl: './createclient.component.scss'
})

export class CreateclientComponent {
  clientForm!: FormGroup;
  contentReady: boolean = false;
  isMobile: boolean = false;
  isAr: boolean = false;
  submitted = false;
  hideClientBalanceFields = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private deviceService: DeviceDetectorService,
    private userService: UserService,
    private clientsService: ClientsService
  ) { }

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobileDevice();
    this.isAr = localStorage.getItem('language') === 'ar';

    this.clientForm = this.fb.group({
      client_name: ['', Validators.required],
      phone_number: ['', [Validators.required, this.realEgyptOrKsaPhoneValidator]],
      another_phone: ['', this.realEgyptOrKsaPhoneValidator],
      address: ['', Validators.required],
      note: [''],
      credit_limit: ['', [Validators.min(0)]],
      opening_balance: ['', [Validators.min(0)]],
      opening_balance_type: [''],
      warehouse_id: [localStorage.getItem('posId')],
      user_id: [localStorage.getItem('user_id')]
    });

    this.loadWarehouseType();

    this.clientForm.get('opening_balance')?.valueChanges.subscribe(() => {
      this.updateOpeningBalanceTypeValidators();
    });
  }

  private loadWarehouseType(): void {
    const warehouseId = Number(localStorage.getItem('posId'));
    if (!warehouseId) return;

    this.userService.getWarehouseType(warehouseId).subscribe({
      next: ({ warehouses_type }) => {
        const type = Number(warehouses_type);
        this.hideClientBalanceFields = [2, 3, 4].includes(type);

        if (this.hideClientBalanceFields) {
          this.clientForm.patchValue({
            credit_limit: '',
            opening_balance: '',
            opening_balance_type: '',
          });
        }

        this.updateOpeningBalanceTypeValidators();
      },
      error: (err) => console.error(err),
    });
  }

  realEgyptOrKsaPhoneValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const rawValue = String(control.value).trim();
    const arabicRegex = /[\u0600-\u06FF\u0660-\u0669\u06F0-\u06F9]/;
    if (arabicRegex.test(rawValue)) {
      return { arabicNotAllowed: true };
    }

    const phone = rawValue.replace(/[\s\-()]/g, '');
    const egyptMobileRegex = /^(01[0125]\d{8}|\+201[0125]\d{8}|00201[0125]\d{8})$/;
    const ksaMobileRegex = /^(05\d{8}|\+9665\d{8}|009665\d{8})$/;

    return egyptMobileRegex.test(phone) || ksaMobileRegex.test(phone)
      ? null
      : { invalidEgyptOrKsaPhone: true };
  };

  private updateOpeningBalanceTypeValidators(): void {
    const openingBalance = Number(this.clientForm.get('opening_balance')?.value || 0);
    const typeControl = this.clientForm.get('opening_balance_type');
    if (!typeControl) return;

    if (openingBalance > 0) {
      typeControl.setValidators([Validators.required]);
    } else {
      typeControl.clearValidators();
    }

    typeControl.updateValueAndValidity({ emitEvent: false });
  }

  shouldShowError(controlName: string): boolean {
    const control = this.clientForm?.get(controlName);
    return !!control && control.invalid && (control.touched || this.submitted);
  }

  getErrorMessage(controlName: string, label: string): string {
    const control = this.clientForm?.get(controlName);

    if (!control || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return this.isAr ? `${label} مطلوب` : `${label} is required`;
    }

    if (control.errors['invalidEgyptOrKsaPhone']) {
      return this.isAr
        ? `${label} لازم يكون رقم موبايل مصري أو سعودي صحيح`
        : `${label} must be a valid Egyptian or Saudi mobile number`;
    }

    if (control.errors['arabicNotAllowed']) {
      return this.isAr
        ? `${label} لازم يكون بأرقام إنجليزية بدون حروف عربية`
        : `${label} must use English digits without Arabic characters`;
    }

    if (control.errors['min']) {
      return this.isAr ? `${label} لازم يكون صفر أو أكبر` : `${label} must be zero or more`;
    }

    return this.isAr ? `${label} غير صحيح` : `${label} is invalid`;
  }

  getMissingRequiredCount(): number {
    if (!this.clientForm) {
      return 0;
    }

    return Object.keys(this.clientForm.controls).filter((controlName) => {
      const control = this.clientForm.get(controlName);
      return control?.hasError('required');
    }).length;
  }

  private validateBeforeSubmit(): boolean {
    this.submitted = true;
    this.updateOpeningBalanceTypeValidators();

    if (this.clientForm.invalid) {
      this.clientForm.markAllAsTouched();

      Swal.fire({
        icon: 'warning',
        title: this.isAr ? 'تأكد من بيانات العميل' : 'Please check client data',
        text: this.isAr ? 'راجع الرسائل تحت الحقول المطلوبة أو غير الصحيحة' : 'Review the messages below the required or invalid fields',
        confirmButtonText: this.isAr ? 'تمام' : 'OK',
      });

      return false;
    }

    return true;
  }

  saveClient(): void {
    if (!this.validateBeforeSubmit()) {
      return;
    }

    this.clientsService.createClient(this.clientForm.value).subscribe({
      next: () => {
        Swal.fire(this.isAr ? 'تم إنشاء العميل بنجاح' : 'Client created successfully');
        if (this.isMobile)
          this.router.navigate(['/myclients.mobile']);
        else
          this.router.navigate(['/myclients']);
      },
      error: (err) => console.error(err)
    });
  }

  resetClientForm(): void {
    this.clientForm.reset();
    this.submitted = false;
  }
}
